// "Bring SpotDrop to your area" — invite + install section on the marketing
// landing page. Fetches (or lazily creates) this device's referral code once,
// then exposes a WhatsApp share deep-link + a copy-link button + the reusable
// InstallCTA button.
//
// Prize/campaign wording is INTENTIONALLY absent: as long as the backend
// reports `campaign.active === false`, this section only shows "Invite friends"
// and the permanent community-growth copy.

import React from "react";
import { motion } from "framer-motion";
import { Share2, Users, Copy, Check, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { fetchMyReferral, buildJoinUrl, buildWhatsAppShareUrl } from "../lib/referral";
import { InstallButton } from "./InstallCTA";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export default function InviteSection() {
  const [code, setCode] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchMyReferral();
      if (!cancelled && data?.code) setCode(data.code);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const joinUrl = code ? buildJoinUrl(code) : "";
  const waUrl = code ? buildWhatsAppShareUrl(code) : "";

  const onCopy = React.useCallback(async () => {
    if (!joinUrl) return;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy the link");
    }
  }, [joinUrl]);

  // Public copy is intentionally generic — no prize wording anywhere.
  // If/when the fuel-voucher campaign activates, the admin can either update
  // this copy via /admin/campaign or we can wire it here from /campaign/public.
  const ctaTitle = "Invite friends";

  return (
    <section
      id="invite"
      className="relative py-20 md:py-28 border-t border-white/5"
      data-testid="invite-section"
    >
      <div className="max-w-5xl mx-auto px-5 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-[10px] md:text-xs uppercase tracking-[0.18em] text-zinc-400">
            <Users className="w-3 h-3 text-[#ccff00]" />
            Community
          </div>
          <h2
            className="font-heading text-3xl md:text-5xl font-semibold tracking-tight mt-5"
            data-testid="invite-title"
          >
            Bring SpotDrop to your area.
          </h2>
          <p
            className="mt-4 md:mt-5 text-base md:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed"
            data-testid="invite-copy"
          >
            The more drivers nearby, the more useful SpotDrop becomes. Share SpotDrop
            with friends and help build a stronger parking community in Malta.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="mt-10 md:mt-12 p-6 md:p-8 rounded-3xl bg-[#141416] border border-white/10"
        >
          <div className="flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
                Your referral link
              </div>
              {loading ? (
                <div
                  className="mt-2 h-6 w-64 bg-white/[0.04] rounded animate-pulse"
                  data-testid="invite-link-loading"
                />
              ) : joinUrl ? (
                <div
                  className="mt-2 font-mono text-sm md:text-base text-white break-all"
                  data-testid="invite-link"
                >
                  {joinUrl}
                </div>
              ) : (
                <div className="mt-2 text-sm text-zinc-500" data-testid="invite-link-error">
                  Your link will appear here once you connect. Please refresh.
                </div>
              )}
            </div>
            <Button
              type="button"
              onClick={onCopy}
              disabled={!joinUrl}
              className="shrink-0 h-11 rounded-full px-5 bg-white/[0.04] border border-white/10 text-white hover:bg-white/[0.08] font-medium"
              data-testid="invite-copy-btn"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-[#ccff00]" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy link
                </>
              )}
            </Button>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a
              href={waUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={!waUrl}
              className={`inline-flex items-center justify-center gap-2 h-12 rounded-full px-6 text-base font-semibold transition-colors ${
                waUrl
                  ? "bg-[#25D366] text-white hover:bg-[#1eb757]"
                  : "bg-white/[0.04] text-zinc-500 pointer-events-none"
              }`}
              data-testid="share-whatsapp-btn"
              aria-label={ctaTitle + " — Share on WhatsApp"}
            >
              <MessageCircle className="w-4 h-4" />
              Share on WhatsApp
            </a>
            <InstallButton
              variant="primary"
              label="Install SpotDrop"
              testId="invite-install-cta"
            />
            <Button
              type="button"
              onClick={onCopy}
              disabled={!joinUrl}
              className="sm:hidden h-12 rounded-full px-6 bg-white/[0.04] border border-white/10 text-white hover:bg-white/[0.08] font-medium"
              data-testid="invite-copy-btn-mobile"
              aria-label="Copy referral link"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Copy link
            </Button>
          </div>

          <p className="mt-5 text-xs text-zinc-500 leading-relaxed">
            No sign-up required. Your referral link is unique to your device and can be
            shared safely with friends.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
